import { http } from '@/api/http';
import type {
  CurrentUser,
  LoginAttemptInfo,
  LoginResult,
  RefreshResult,
  SessionInfo,
  TwoFactorEnableResult,
  TwoFactorSetup,
} from '@/types/auth';

/**
 * Auth endpoints (backend: src/modules/auth/auth.controller.ts). The refresh
 * token travels in an httpOnly cookie; only the access token is handled in JS.
 */
export const authApi = {
  login: (body: { email: string; password: string }) => http.post<LoginResult>('/auth/login', body),

  loginMfa: (body: { mfaToken: string; code: string }) =>
    http.post<LoginResult>('/auth/login/mfa', body),

  refresh: () => http.post<RefreshResult>('/auth/refresh', {}),

  me: () => http.get<CurrentUser>('/auth/me'),

  logout: () => http.post<{ success: boolean }>('/auth/logout', {}),

  logoutAll: () => http.post<{ success: boolean }>('/auth/logout-all', {}),

  forgotPassword: (body: { email: string }) =>
    http.post<{ success: boolean }>('/auth/forgot-password', body),

  resetPassword: (body: { token: string; password: string }) =>
    http.post<{ success: boolean }>('/auth/reset-password', body),

  verifyEmail: (body: { token: string }) =>
    http.post<{ success: boolean }>('/auth/verify-email', body),

  changePassword: (body: { currentPassword: string; newPassword: string }) =>
    http.post<{ success: boolean }>('/auth/change-password', body),

  resendVerification: () => http.post<{ success: boolean }>('/auth/resend-verification', {}),

  // Sessions & history
  sessions: () => http.get<SessionInfo[]>('/auth/sessions'),
  revokeSession: (id: string) => http.del<{ success: boolean }>(`/auth/sessions/${id}`),
  revokeOtherSessions: () => http.del<{ revoked: number }>('/auth/sessions'),
  loginHistory: () => http.get<LoginAttemptInfo[]>('/auth/login-history'),

  // Two-factor
  setup2fa: () => http.post<TwoFactorSetup>('/auth/2fa/setup', {}),
  enable2fa: (body: { code: string }) => http.post<TwoFactorEnableResult>('/auth/2fa/enable', body),
  disable2fa: (body: { password: string }) =>
    http.post<{ success: boolean }>('/auth/2fa/disable', body),
  regenerateBackupCodes: () => http.post<TwoFactorEnableResult>('/auth/2fa/backup-codes', {}),
};
